import { useTranslations } from '@vocab/react';
import { RadioGroup, RadioItem } from 'braid-design-system';
import React, { type FormEvent, useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { TEALIUM_CAJA_PAY_TYPE } from 'src/app/shared/constants/dispatcherId';
import { PAY_TYPE_PRESSED } from 'src/app/shared/dataAnalytics/schema/constants';
import { usePayTypeTaxonomy } from 'src/app/shared/taxonomies/usePayTypeTaxonomy';
import getTrackingAdapter from 'src/app/v1/dataAnalytics';
import dispatchTealium from 'src/app/v1/state/dispatcher/tealium';
import { actionUpdatePayType } from 'src/app/v1/state/modules/formData/payType';
import type { AppState } from 'src/types/appState';
import type { Option } from 'src/types/formFields';

import { usePayType } from '../../hooks/usePayType';

import translations from './.vocab';

export const PayType = () => {
  const { payType } = useSelector(
    (state: AppState) => ({
      payType: state.formData.payType,
    }),
    shallowEqual,
  );

  const dispatch = useDispatch();

  const defaultPayType = usePayType();
  const [inputValue, setInputValue] = useState<string>(defaultPayType);

  const { t } = useTranslations(translations);

  const { payTypes }: { payTypes: Option[] } = usePayTypeTaxonomy();

  useEffect(() => {
    if (payType) {
      setInputValue(payType);
    }
  }, [payType]);

  const handleChange = (event: FormEvent<HTMLInputElement>): void => {
    const { value } = event.currentTarget;

    setInputValue(value);

    dispatch(actionUpdatePayType(value));
    dispatch(dispatchTealium(TEALIUM_CAJA_PAY_TYPE, value));

    const eventData = {
      eventName: PAY_TYPE_PRESSED,
      objectInteraction: `pay-type-${value}`,
      jobSalaryType: value,
    };
    getTrackingAdapter.sendLinkEvent(eventData);
  };

  return (
    <RadioGroup
      id="PayTypeRadioButtonGroup"
      label={t('pay-type-label')}
      onChange={handleChange}
      value={inputValue}
      reserveMessageSpace
    >
      {payTypes.map(({ text, value }: Option, index: number) => (
        <RadioItem key={`pay-type-${index}`} label={text} value={value} />
      ))}
    </RadioGroup>
  );
};
